import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { CpuChipIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import Button from 'components/inputs/button';
import { CondenserType, ZoneItemType, ZoneType } from 'data/types';
import {
  moveZoneItemToZone,
  moveZoneToCondenser
} from 'features/configurations';
import { useMemo, useState } from 'react';
import {
  condenserHasZoneCapacity,
  condenserZoneCapacityMessage
} from 'utils/condenser-zone-capacity';
import { notification } from 'utils/ui';
import { generateZoneColor } from 'utils/zone-outline';

type ZoneCondenserMapProps = Readonly<{
  activeZoneId: string | null;
  isOnZoneStep?: boolean;
  readOnly?: boolean;
  variant?: 'condensers' | 'flat';
  onSetActiveZone: (zoneId: string) => void;
  onRemoveSpaceFromZone: (
    spaceId: string,
    zoneId: string,
    condenserId: string
  ) => void;
  onDeleteZone: (zoneId: string, condenserId: string) => void;
  onAddZoneToCondenser: (condenserId: string) => void;
  onAddZone?: () => void;
}>;

type ZoneDragData = {
  type: 'zone';
  zoneId: string;
  condenserId: string;
};

type SpaceDragData = {
  type: 'space';
  zoneItemId: string;
  zoneId: string;
  condenserId: string;
  name: string;
};

type DragData = ZoneDragData | SpaceDragData;

type GlobalZoneEntry = {
  zone: ZoneType;
  condenserId: string;
  globalIndex: number;
};

type DraggableSpaceChipProps = Readonly<{
  item: ZoneItemType;
  zoneId: string;
  condenserId: string;
  onRemoveSpaceFromZone?: (
    spaceId: string,
    zoneId: string,
    condenserId: string
  ) => void;
}>;

function DraggableSpaceChip({
  item,
  zoneId,
  condenserId,
  onRemoveSpaceFromZone
}: DraggableSpaceChipProps) {
  const dragData: SpaceDragData = {
    type: 'space',
    zoneItemId: item.uniqueId,
    zoneId,
    condenserId,
    name: item.name
  };

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `space-drag-${item.uniqueId}`,
    data: dragData
  });

  return (
    <span
      ref={setNodeRef}
      className={clsx(
        'inline-flex cursor-grab items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-3 pr-1 text-xs text-gray-700',
        isDragging && 'opacity-40'
      )}
      {...listeners}
      {...attributes}
    >
      {item.name}
      {onRemoveSpaceFromZone && (
        <button
          type='button'
          className='rounded-full p-0.5 hover:bg-gray-200'
          onClick={(e) => {
            e.stopPropagation();
            onRemoveSpaceFromZone(item.structureItemId, zoneId, condenserId);
          }}
        >
          <XMarkIcon className='h-3 w-3 text-gray-500' />
        </button>
      )}
    </span>
  );
}

function SpaceChipOverlay({ name }: Readonly<{ name: string }>) {
  return (
    <span className='inline-flex items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-3 pr-1 text-xs text-gray-700 shadow-lg'>
      {name}
    </span>
  );
}

type ZoneCardContentProps = Readonly<{
  zone: ZoneType;
  condenserId: string;
  isActive?: boolean;
  isOnZoneStep?: boolean;
  onSetActiveZone?: (zoneId: string) => void;
  onRemoveSpaceFromZone?: (
    spaceId: string,
    zoneId: string,
    condenserId: string
  ) => void;
  onDeleteZone?: (zoneId: string, condenserId: string) => void;
  draggableSpaces?: boolean;
}>;

function ZoneCardContent({
  zone,
  condenserId,
  isActive = false,
  isOnZoneStep = false,
  onSetActiveZone,
  onRemoveSpaceFromZone,
  onDeleteZone,
  draggableSpaces = false
}: ZoneCardContentProps) {
  const spaceCount = zone.zoneItems.length;

  return (
    <div
      role='button'
      tabIndex={0}
      className='relative w-full cursor-pointer text-left'
      onClick={() => onSetActiveZone?.(zone.uniqueId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onSetActiveZone?.(zone.uniqueId);
        }
      }}
    >
      {onDeleteZone && (
        <button
          type='button'
          className='absolute -right-1 -top-1 rounded-full p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
          onClick={(e) => {
            e.stopPropagation();
            onDeleteZone(zone.uniqueId, condenserId);
          }}
        >
          <XMarkIcon className='h-4 w-4' />
        </button>
      )}
      <div>
        <span className='font-medium text-gray-900'>{zone.name}</span>
      </div>
      {spaceCount > 0 ? (
        <div className='mt-2 flex flex-wrap gap-1'>
          {zone.zoneItems.map((item: ZoneItemType) =>
            draggableSpaces ? (
              <DraggableSpaceChip
                key={item.uniqueId}
                item={item}
                zoneId={zone.uniqueId}
                condenserId={condenserId}
                onRemoveSpaceFromZone={onRemoveSpaceFromZone}
              />
            ) : (
              <span
                key={item.uniqueId}
                className='inline-flex items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-3 pr-1 text-xs text-gray-700'
              >
                {item.name}
              </span>
            )
          )}
        </div>
      ) : (
        <div className='mt-2 text-sm text-gray-500'>
          {isOnZoneStep && isActive
            ? 'Click spaces on the grid to add them'
            : 'Empty'}
        </div>
      )}
    </div>
  );
}

type DraggableZoneCardProps = Readonly<{
  zone: ZoneType;
  condenserId: string;
  globalIndex: number;
  isActive: boolean;
  isOnZoneStep: boolean;
  isSpaceDropTarget: boolean;
  readOnly: boolean;
  zoneDragDisabled?: boolean;
  onSetActiveZone: (zoneId: string) => void;
  onRemoveSpaceFromZone: (
    spaceId: string,
    zoneId: string,
    condenserId: string
  ) => void;
  onDeleteZone: (zoneId: string, condenserId: string) => void;
}>;

function DraggableZoneCard({
  zone,
  condenserId,
  globalIndex,
  isActive,
  isOnZoneStep,
  isSpaceDropTarget,
  readOnly,
  zoneDragDisabled = false,
  onSetActiveZone,
  onRemoveSpaceFromZone,
  onDeleteZone
}: DraggableZoneCardProps) {
  const zoneDragData: ZoneDragData = {
    type: 'zone',
    zoneId: zone.uniqueId,
    condenserId
  };

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    isDragging
  } = useDraggable({
    id: `zone-drag-${zone.uniqueId}`,
    data: zoneDragData,
    disabled: readOnly || zoneDragDisabled
  });

  const { setNodeRef: setDroppableRef } = useDroppable({
    id: `zone-drop-${zone.uniqueId}`,
    data: { type: 'zone-drop', zoneId: zone.uniqueId, condenserId },
    disabled: readOnly
  });

  const setNodeRef = (node: HTMLElement | null) => {
    setDraggableRef(node);
    setDroppableRef(node);
  };

  const zoneColor = generateZoneColor(globalIndex);
  const activeBgColor = zoneColor.outline
    .replace('hsl(', 'hsla(')
    .replace(')', ', 0.15)');

  let backgroundColor: string | undefined;
  if (isSpaceDropTarget) {
    backgroundColor = '#dbeafe';
  } else if (isActive) {
    backgroundColor = activeBgColor;
  }

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'rounded-lg p-3 transition-colors',
        readOnly || zoneDragDisabled ? 'cursor-pointer' : 'cursor-grab',
        isDragging && 'opacity-40'
      )}
      style={{
        borderWidth: '3px',
        borderStyle: 'solid',
        borderColor: isSpaceDropTarget ? '#60a5fa' : zoneColor.outline,
        backgroundColor
      }}
      {...listeners}
      {...attributes}
    >
      <ZoneCardContent
        zone={zone}
        condenserId={condenserId}
        isActive={isActive}
        isOnZoneStep={isOnZoneStep}
        onSetActiveZone={onSetActiveZone}
        onRemoveSpaceFromZone={readOnly ? undefined : onRemoveSpaceFromZone}
        onDeleteZone={readOnly ? undefined : onDeleteZone}
        draggableSpaces={!readOnly}
      />
    </div>
  );
}

type FlatZoneListProps = Readonly<{
  zones: GlobalZoneEntry[];
  activeZoneId: string | null;
  isOnZoneStep: boolean;
  overZoneId: string | null;
  addZoneDisabled: boolean;
  readOnly: boolean;
  onSetActiveZone: (zoneId: string) => void;
  onRemoveSpaceFromZone: (
    spaceId: string,
    zoneId: string,
    condenserId: string
  ) => void;
  onDeleteZone: (zoneId: string, condenserId: string) => void;
  onAddZone?: () => void;
}>;

function FlatZoneList({
  zones,
  activeZoneId,
  isOnZoneStep,
  overZoneId,
  addZoneDisabled,
  readOnly,
  onSetActiveZone,
  onRemoveSpaceFromZone,
  onDeleteZone,
  onAddZone
}: FlatZoneListProps) {
  return (
    <div className='flex flex-col gap-3'>
      {zones.map((entry) => (
        <DraggableZoneCard
          key={entry.zone.uniqueId}
          zone={entry.zone}
          condenserId={entry.condenserId}
          globalIndex={entry.globalIndex}
          isActive={activeZoneId === entry.zone.uniqueId}
          isOnZoneStep={isOnZoneStep}
          isSpaceDropTarget={overZoneId === entry.zone.uniqueId}
          readOnly={readOnly}
          zoneDragDisabled
          onSetActiveZone={onSetActiveZone}
          onRemoveSpaceFromZone={onRemoveSpaceFromZone}
          onDeleteZone={onDeleteZone}
        />
      ))}
      {!readOnly && onAddZone && (
        <Button
          color='white'
          text='Add Zone'
          onClick={onAddZone}
          icon={PlusIcon}
          disabled={addZoneDisabled}
          fullWidth
        />
      )}
    </div>
  );
}

type DroppableCondenserGroupProps = Readonly<{
  condenser: CondenserType;
  zones: GlobalZoneEntry[];
  activeZoneId: string | null;
  isOnZoneStep: boolean;
  isOver: boolean;
  overZoneId: string | null;
  addZoneDisabled: boolean;
  readOnly: boolean;
  onSetActiveZone: (zoneId: string) => void;
  onRemoveSpaceFromZone: (
    spaceId: string,
    zoneId: string,
    condenserId: string
  ) => void;
  onDeleteZone: (zoneId: string, condenserId: string) => void;
  onAddZoneToCondenser: (condenserId: string) => void;
}>;

function DroppableCondenserGroup({
  condenser,
  zones,
  activeZoneId,
  isOnZoneStep,
  isOver,
  overZoneId,
  addZoneDisabled,
  readOnly,
  onSetActiveZone,
  onRemoveSpaceFromZone,
  onDeleteZone,
  onAddZoneToCondenser
}: DroppableCondenserGroupProps) {
  const { setNodeRef } = useDroppable({
    id: `condenser-drop-${condenser.uniqueId}`,
    data: { condenserId: condenser.uniqueId },
    disabled: readOnly
  });

  return (
    <div className='flex flex-col'>
      <div className='flex'>
        <div className='flex flex-col pr-2 pt-2'>
          <div className='h-3 w-3 rounded-full bg-gray-300' />
          <div className='ml-[4px] grow border-l-4 border-gray-300' />
        </div>
        <div className='flex grow flex-wrap items-center gap-2'>
          <CpuChipIcon className='h-6 w-6 text-gray-800' />
          <span className='text-lg font-semibold'>{condenser.name}</span>
        </div>
      </div>
      <div
        ref={setNodeRef}
        className={clsx(
          'flex flex-col transition-colors',
          isOver ? 'border-blue-400 bg-blue-50' : 'border-transparent'
        )}
      >
        {zones.map((entry, zoneIndex) => (
          <div key={entry.zone.uniqueId} className='flex items-start gap-2'>
            <div className='relative flex items-start self-stretch'>
              {zoneIndex < zones.length - 1 && (
                <div className='absolute left-[4px] h-full border-l-4 border-gray-300' />
              )}
              <div className='mb-[4px] ml-[4px] w-5 rounded-bl-2xl border-b-4 border-l-4 border-gray-300 pt-4' />
              <div className='mt-[12px] h-3 w-3 rounded-full bg-gray-300' />
            </div>
            <div className='flex grow flex-col pt-3'>
              <DraggableZoneCard
                zone={entry.zone}
                condenserId={entry.condenserId}
                globalIndex={entry.globalIndex}
                isActive={activeZoneId === entry.zone.uniqueId}
                isOnZoneStep={isOnZoneStep}
                isSpaceDropTarget={overZoneId === entry.zone.uniqueId}
                readOnly={readOnly}
                onSetActiveZone={onSetActiveZone}
                onRemoveSpaceFromZone={onRemoveSpaceFromZone}
                onDeleteZone={onDeleteZone}
              />
            </div>
          </div>
        ))}
        {!readOnly && (
          <div className='flex-1 pl-11 pt-3'>
            <Button
              color='white'
              text='Add Zone'
              onClick={() => onAddZoneToCondenser(condenser.uniqueId)}
              onDisabledClick={
                condenserHasZoneCapacity(condenser)
                  ? undefined
                  : () =>
                      notification(
                        condenserZoneCapacityMessage(condenser),
                        'warning'
                      )
              }
              icon={PlusIcon}
              disabled={addZoneDisabled || !condenserHasZoneCapacity(condenser)}
              fullWidth
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ZoneCondenserMap({
  activeZoneId,
  isOnZoneStep = false,
  readOnly = false,
  variant = 'condensers',
  onSetActiveZone,
  onRemoveSpaceFromZone,
  onDeleteZone,
  onAddZoneToCondenser,
  onAddZone
}: ZoneCondenserMapProps) {
  const dispatch = useAppDispatch();
  const { configurations, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const currentConfig = configurations[activeConfig];
  const [dragData, setDragData] = useState<DragData | null>(null);
  const [overCondenserId, setOverCondenserId] = useState<string | null>(null);
  const [overZoneId, setOverZoneId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 5 } })
  );

  const condensersWithZones = useMemo(() => {
    if (!currentConfig) return [];
    let globalIndex = 0;
    return currentConfig.condensers.map((condenser) => {
      const zones: GlobalZoneEntry[] = condenser.zones.map((zone) => {
        const entry: GlobalZoneEntry = {
          zone,
          condenserId: condenser.uniqueId,
          globalIndex
        };
        globalIndex += 1;
        return entry;
      });
      return { condenser, zones };
    });
  }, [currentConfig]);

  const hasAnyEmptyZone = useMemo(
    () =>
      condensersWithZones.some((group) =>
        group.zones.some((entry) => entry.zone.zoneItems.length === 0)
      ),
    [condensersWithZones]
  );

  const flatZones = useMemo(
    () => condensersWithZones.flatMap((group) => group.zones),
    [condensersWithZones]
  );

  const draggedZoneEntry = useMemo(() => {
    if (dragData?.type !== 'zone') return null;
    for (const group of condensersWithZones) {
      for (const entry of group.zones) {
        if (entry.zone.uniqueId === dragData.zoneId) {
          return entry;
        }
      }
    }
    return null;
  }, [dragData, condensersWithZones]);

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as DragData | undefined;
    if (data) {
      setDragData(data);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const overDataCurrent = event.over?.data.current as
      | Record<string, string>
      | undefined;
    if (!overDataCurrent) {
      setOverCondenserId(null);
      setOverZoneId(null);
      return;
    }

    if (overDataCurrent.type === 'zone-drop') {
      setOverZoneId(overDataCurrent.zoneId ?? null);
      setOverCondenserId(null);
    } else if (overDataCurrent.condenserId) {
      setOverCondenserId(overDataCurrent.condenserId);
      setOverZoneId(null);
    } else {
      setOverCondenserId(null);
      setOverZoneId(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDragData(null);
    setOverCondenserId(null);
    setOverZoneId(null);

    if (!over || !currentConfig) return;

    const activeData = active.data.current as DragData | undefined;
    if (!activeData) return;

    const overDataCurrent = over.data.current as
      | Record<string, string>
      | undefined;
    if (!overDataCurrent) return;

    if (activeData.type === 'zone') {
      const toCondenserId = overDataCurrent.condenserId;
      if (!toCondenserId || activeData.condenserId === toCondenserId) return;

      dispatch(
        moveZoneToCondenser({
          zoneId: activeData.zoneId,
          fromCondenserId: activeData.condenserId,
          toCondenserId,
          configurationId: currentConfig.uniqueId
        })
      );
    } else if (activeData.type === 'space') {
      const toZoneId = overDataCurrent.zoneId;
      const toCondenserId = overDataCurrent.condenserId;
      if (!toZoneId || !toCondenserId) return;
      if (activeData.zoneId === toZoneId) return;

      dispatch(
        moveZoneItemToZone({
          zoneItemId: activeData.zoneItemId,
          fromZoneId: activeData.zoneId,
          fromCondenserId: activeData.condenserId,
          toZoneId,
          toCondenserId,
          configurationId: currentConfig.uniqueId
        })
      );
    }
  };

  if (!currentConfig) return null;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {variant === 'flat' ? (
        <FlatZoneList
          zones={flatZones}
          activeZoneId={activeZoneId}
          isOnZoneStep={isOnZoneStep}
          overZoneId={overZoneId}
          addZoneDisabled={hasAnyEmptyZone}
          readOnly={readOnly}
          onSetActiveZone={onSetActiveZone}
          onRemoveSpaceFromZone={onRemoveSpaceFromZone}
          onDeleteZone={onDeleteZone}
          onAddZone={onAddZone}
        />
      ) : (
        <div className='flex flex-col gap-2'>
          {condensersWithZones.map(({ condenser, zones }) => (
            <DroppableCondenserGroup
              key={condenser.uniqueId}
              condenser={condenser}
              zones={zones}
              activeZoneId={activeZoneId}
              isOnZoneStep={isOnZoneStep}
              isOver={overCondenserId === condenser.uniqueId}
              overZoneId={overZoneId}
              addZoneDisabled={hasAnyEmptyZone}
              readOnly={readOnly}
              onSetActiveZone={onSetActiveZone}
              onRemoveSpaceFromZone={onRemoveSpaceFromZone}
              onDeleteZone={onDeleteZone}
              onAddZoneToCondenser={onAddZoneToCondenser}
            />
          ))}
        </div>
      )}
      <DragOverlay>
        {draggedZoneEntry && (
          <div
            className='w-64 rounded-lg bg-white p-3 shadow-lg'
            style={{
              borderWidth: '3px',
              borderStyle: 'solid',
              borderColor: generateZoneColor(draggedZoneEntry.globalIndex)
                .outline
            }}
          >
            <ZoneCardContent
              zone={draggedZoneEntry.zone}
              condenserId={draggedZoneEntry.condenserId}
            />
          </div>
        )}
        {dragData?.type === 'space' && (
          <SpaceChipOverlay name={dragData.name} />
        )}
      </DragOverlay>
    </DndContext>
  );
}
